import React, { useState, useCallback, useMemo, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useExpenseData } from "../hooks/useExpenseData";
import { downloadCSV } from "../utils/ExpenseHelpers";
import ExpenseForm from "../components/ExpenseForm";
import ExpenseLedger from "../components/ExpenseLedger";
import PersonalExpenseSection from "../components/PersonalExpenseSection";
import IncomingRequestsPanel from "../components/IncomingRequestsPanel";
import {
  canEditTransaction,
  canDeleteTransaction, 
  deletePersonalExpense,
  updatePersonalExpense
} from "../utils/TransactionActions";

const ITEMS_PER_PAGE = 10;

function Expense() {
  const { 
    user, 
    currentAuthId,
    balance, 
    ledger = [], 
    pendingIncomingRequests = [], 
    users = [], 
    refreshAll, 
    fetchTransactions, 
    userMap = {} 
  } = useExpenseData();

  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(""); 
  
  const [expenseForm, setExpenseForm] = useState({ amount: "", note: "", project_id: "" });
  const [currentPage, setCurrentPage] = useState(1);

  const isSuperAdmin = user?.role === "super_admin";

  // Fetch Projects list from database matching relational schema
  // Fetch Projects list from database matching relational schema
  useEffect(() => {
    async function fetchProjects() {
      if (!user) return;

      try {
        let assignedUserId = null;

        if (user.role !== "super_admin") {
          const { data: userRecord, error: userError } = await supabase
            .from("users")
            .select("id")
            .eq("auth_id", user.id)
            .single();

          if (userError) {
            console.error("Error finding system user mapping:", userError);
            return;
          }

          assignedUserId = userRecord.id;
        }

        // Base query selections mapping clean structural attributes
        let query = supabase
          .from("projects")
          .select(`
            project_id,
            project_name,
            project_code,
            status,
            project_meta (
              meta_id,
              client_name
            ),
            project_assignments!inner (
              user_id
            )
          `);

        // If not a super_admin, filter only where the user is part of project_assignments junction table
        if (user.role !== "super_admin") {
          query = query.eq("project_assignments.user_id", assignedUserId);
        }

        const { data, error } = await query.order("project_name");

        if (error) {
          console.error("Error fetching projects:", error);
          return;
        }

        console.log("Filtered assigned projects for dropdown:", data);
        setProjects(data || []);
      } catch (err) {
        console.error("Catch handler error in projects lookup:", err);
      }
    }

    fetchProjects();
  }, [user]);
  const getName = useCallback((uid) => {
    if (!uid) return "System";
    if (userMap?.[String(uid)]) return userMap[String(uid)];
    if (Array.isArray(users)) {
      const found = users.find((u) => String(u.id) === String(uid) || String(u.auth_id) === String(uid));
      if (found) return found.name;
    }
    return `User (${String(uid).slice(0, 6)}...)`;
  }, [userMap, users]);

  // Project lookup helper mapping matching exact columns including parent meta configurations
  const getProjectDetails = useCallback((projId) => {
    if (!projId) return { name: "No Project", code: "", clientName: "N/A", status: "N/A" };
    const found = projects.find(p => p.project_id === projId);
    return found ? { 
      name: found.project_name, 
      code: found.project_code,
      clientName: found.project_meta?.client_name || "N/A",
      status: found.status || "Backlog"
    } : { name: "Unknown", code: "", clientName: "N/A", status: "N/A" };
  }, [projects]);

  // --- CRITERIA FOR PERSONAL EXPENSES (Only "In Progress" or "Review") ---
  const personalExpenseProjects = useMemo(() => {
    return projects.filter(p => {
      const normalizedStatus = p.status?.toLowerCase();
      return normalizedStatus === "in progress" || normalizedStatus === "review";
    });
  }, [projects]);

  const submitExpenditure = async () => {
    const actingUserId = currentAuthId || user?.id;
    if (!selectedTx || !expenseForm.amount || !actingUserId) return;
    
    const targetProj = projects.find(p => p.project_id === expenseForm.project_id);
    if (targetProj && targetProj.status?.toLowerCase() === "done") {
      alert("Transaction blocked: This project has been marked as Completed / Done.");
      return;
    }

    const { error } = await supabase.from("wallet_ledger").insert({
      transaction_id: selectedTx.id, 
      user_id: actingUserId, 
      counterparty_id: selectedTx.from_user_id,
      amount: Number(expenseForm.amount), 
      entry_type: "debit", 
      note: expenseForm.note.trim() || "Expenditure",
      project_id: expenseForm.project_id || null, 
      created_at: new Date().toISOString()
    });

    if (error) {
      alert(`Failed to add expenditure: ${error.message}`);
      return;
    }

    setExpenseForm({ amount: "", note: "", project_id: "" });
    setShowModal(false);
    refreshAll();
  };

  const handleDelete = async (item) => {
    const targetAuthorizerId = item.user_id || currentAuthId || user?.id;
    
    if (!canDeleteTransaction(item, targetAuthorizerId)) {
      alert("Accepted core group transactions cannot be altered to preserve double-entry integrity.");
      return;
    }

    try {
      await deletePersonalExpense(item.id, targetAuthorizerId);
      refreshAll();
    } catch (err) {
      alert(`Deletion failed: ${err.message}`);
    }
  };

  const handleEdit = async (item, amount, note, project_id) => {
    const targetAuthorizerId = item.user_id || currentAuthId || user?.id;

    if (!canEditTransaction(item, targetAuthorizerId)) {
      alert("Accepted core group transactions cannot be altered.");
      return;
    }

    try {
      await updatePersonalExpense(item.id, amount, note, targetAuthorizerId, project_id);
      refreshAll();
    } catch (err) {
      alert(`Update failed: ${err.message}`);
    }
  };

  const projectExpenditureMap = useMemo(() => {
    const totals = {};
    ledger.forEach((item) => {
      if (item.project_id) {
        totals[item.project_id] = (totals[item.project_id] || 0) + Number(item.amount || 0);
      }
    });
    return totals;
  }, [ledger]);

  // --- EXPLICIT COLUMN DATA GENERATION IN REPORT DOWNLOAD ---
  const downloadAllCSV = () => {
    const updatedLedger = ledger.map((item) => {
      const proj = getProjectDetails(item.project_id);
      const projectTotalExpenditure = item.project_id ? (projectExpenditureMap[item.project_id] || 0) : 0;

      return {
        Date: new Date(item.created_at).toLocaleDateString(),
        Time: new Date(item.created_at).toLocaleTimeString(),
        Group: item.transaction_id ? item.transaction_id.slice(0, 8) : "Personal",
        "Client Name": proj.clientName, 
        "Project Name": proj.name,
        "Project Code": proj.code || "-",
        "Project Status": proj.status,
        User: getName(item.user_id), 
        Type: item.entry_type,
        Amount: item.amount,
        "Total Project Expenditure": projectTotalExpenditure, 
        Counterparty: item.counterparty_id ? getName(item.counterparty_id) : "-",
        Note: item.note || ""
      };
    });
    downloadCSV(updatedLedger, "all_ledger_expenditure_report.csv");
  };

  // --- SCOPED LEDGER ROWS FILTER BAR ---
  const filteredLedger = useMemo(() => {
    return ledger.filter(item => {
      // 🔐 LEDGER VISIBILITY FILTER RULE
      // If the user is not a super admin, hide transactions that do not belong to their assigned projects
      if (!isSuperAdmin) {
        const isProjectAssignedToMe = projects.some(p => p.project_id === item.project_id);
        if (!isProjectAssignedToMe) return false;
      }

      if (selectedProjectId && item.project_id !== selectedProjectId) {
        return false;
      }

      const s = search.toLowerCase().trim();
      if (!s) return true;

      const proj = getProjectDetails(item.project_id);
      return (
        item.note?.toLowerCase().includes(s) ||
        String(item.amount).includes(s) ||
        getName(item.user_id).toLowerCase().includes(s) ||
        proj.name.toLowerCase().includes(s) ||
        proj.code?.toLowerCase().includes(s) ||
        proj.clientName.toLowerCase().includes(s) 
      );
    });
  }, [ledger, search, selectedProjectId, getName, getProjectDetails, projects, isSuperAdmin]);

  const totalPages = Math.ceil(filteredLedger.length / ITEMS_PER_PAGE) || 1;
  const normalizedCurrentPage = Math.min(currentPage, totalPages);

  const currentTransactions = useMemo(() => {
    const indexOfLastItem = normalizedCurrentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    return filteredLedger.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredLedger, normalizedCurrentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 500, behavior: "smooth" });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1); 
  };

  return (
    <>
      <div style={styles.container}>
        
        {/* 1. BALANCE DISPLAY */}
        <div style={styles.balanceCard}>
          <p style={styles.balanceLabel}>Available Balance</p>
          <h2 style={styles.balanceAmount}>₹{balance ? balance.toLocaleString() : 0}</h2>
        </div>

        {/* 2. PERSONAL EXPENDITURE SECTION */}
        <PersonalExpenseSection 
          user={user} 
          ledger={ledger} 
          getName={getName} 
          refreshAll={refreshAll} 
          allowedProjects={personalExpenseProjects} 
        />
        
        <ExpenseForm user={user} users={users} projects={projects} fetchTransactions={fetchTransactions} />

        {/* 3. INCOMING REQUESTS PANEL */}
        <IncomingRequestsPanel pendingIncomingRequests={pendingIncomingRequests} user={user} getName={getName} refreshAll={refreshAll} />

        {/* 4. LEDGER SECTION */}
        <div style={styles.ledgerHeader}>
          <h3 style={{ margin: 0 }}>Ledger</h3>
          <button onClick={downloadAllCSV} style={styles.csvButton}>
            Download All CSV
          </button>
        </div>

        {/* COMPACT FILTER CONTROLS BAR */}
        <div style={styles.filterBar}>
          <input 
            placeholder="Search ledger..." 
            value={search} 
            onChange={handleSearchChange} 
            style={styles.searchInput} 
          />
          <select
            value={selectedProjectId}
            onChange={(e) => { setSelectedProjectId(e.target.value); setCurrentPage(1); }}
            style={styles.projectDropdownFilter}
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.project_id} value={p.project_id}>
                {p.project_meta?.client_name ? `(${p.project_meta.client_name}) ` : ""}
                {p.project_code ? `[${p.project_code}] ` : ""}{p.project_name}
              </option>
            ))}
          </select>
        </div>

        <ExpenseLedger 
          ledger={currentTransactions} 
          user={user} 
          userMap={userMap} 
          users={users} 
          projects={projects}
          search="" 
          setSelectedTx={setSelectedTx} 
          setShowModal={setShowModal} 
          onEdit={handleEdit} 
          onDelete={handleDelete}
          canEditTransaction={canEditTransaction} 
          canDeleteTransaction={canDeleteTransaction}
        />

        {totalPages > 1 && (
          <div style={styles.paginationRow}>
            <button 
              disabled={normalizedCurrentPage === 1} 
              onClick={() => handlePageChange(normalizedCurrentPage - 1)} 
              style={{
                ...styles.pageButton, 
                cursor: normalizedCurrentPage === 1 ? "not-allowed" : "pointer"
              }}
            >
              Previous
            </button>
            <span style={styles.pageDetails}>
              Page <strong>{normalizedCurrentPage}</strong> of {totalPages}
            </span>
            <button 
              disabled={normalizedCurrentPage === totalPages} 
              onClick={() => handlePageChange(normalizedCurrentPage + 1)} 
              style={{
                ...styles.pageButton, 
                cursor: normalizedCurrentPage === totalPages ? "not-allowed" : "pointer"
              }}
            >
              Next
            </button>
          </div>
        )}

        {/* EXPENDITURE ADDITION MODAL PANEL FOR INCOMING PAYMENTS */}
        {showModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h3>Add Expenditure</h3>
              
              <label style={styles.modalLabel}>Amount</label>
              <input type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} style={styles.modalInput} />
              
              <label style={styles.modalLabel}>Link Project</label>
              <select 
                value={expenseForm.project_id} 
                onChange={(e) => setExpenseForm({ ...expenseForm, project_id: e.target.value })}
                disabled={!!selectedTx?.from_user_id} 
                style={{
                  ...styles.modalInput,
                  backgroundColor: !!selectedTx?.from_user_id ? "#f3f4f6" : "#fff",
                  cursor: !!selectedTx?.from_user_id ? "not-allowed" : "default"
                }}
              >
                <option value="">-- No Project Linked --</option>
                {projects.map(p => {
                  const isDone = p.status?.toLowerCase() === "done";
                  const clientPrefix = p.project_meta?.client_name ? `${p.project_meta.client_name} - ` : "";
                  return (
                    <option key={p.project_id} value={p.project_id} disabled={isDone}>
                      {clientPrefix}{p.project_code ? `[${p.project_code}] ` : ""}{p.project_name} {isDone ? "🔒 (Completed)" : ""}
                    </option>
                  );
                })}
              </select>

              <label style={styles.modalLabel}>Note</label>
              <input placeholder="Note" value={expenseForm.note} onChange={(e) => setExpenseForm({ ...expenseForm, note: e.target.value })} style={styles.modalInput} />
              
              <button onClick={submitExpenditure} style={styles.modalSaveButton}>Save</button>
              <button onClick={() => setShowModal(false)} style={styles.modalCancelButton}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const styles = {
  container: { padding: 20, maxWidth: 900, margin: "auto", fontFamily: "sans-serif" },
  balanceCard: { background: "#1f2937", color: "#fff", padding: "25px", borderRadius: 12, marginBottom: 20 },
  balanceLabel: { margin: 0, opacity: 0.8, fontSize: "14px" },
  balanceAmount: { margin: "5px 0 0 0", fontSize: "32px" },
  ledgerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 30, marginBottom: 15 },
  csvButton: { background: "#4b5563", color: "#fff", padding: "6px 12px", border: "none", borderRadius: 6, cursor: "pointer" },
  filterBar: { display: "flex", gap: "12px", marginBottom: 20, width: "100%" },
  searchInput: { flex: 2, padding: 12, borderRadius: 8, border: "1px solid #ddd", boxSizing: "border-box" },
  projectDropdownFilter: { flex: 1, padding: 12, borderRadius: 8, border: "1px solid #ddd", backgroundColor: "#fff", boxSizing: "border-box" },
  paginationRow: { display: "flex", justifyContent: "center", alignItems: "center", gap: "15px", marginTop: "20px", paddingBottom: "40px" },
  pageButton: { padding: "8px 16px", borderRadius: 6, border: "1px solid #ddd", background: "#fff" },
  pageDetails: { fontSize: "14px", color: "#666" },
  modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modalContent: { background: "#fff", padding: 25, borderRadius: 12, width: 350 },
  modalLabel: { display: "block", fontSize: "12px", fontWeight: "bold", color: "#555", marginBottom: 4 },
  modalInput: { width: "100%", padding: 10, marginBottom: 15, borderRadius: 6, border: "1px solid #ccc", boxSizing: "border-box" },
  modalSaveButton: { width: "100%", padding: 12, background: "#f59e0b", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" },
  modalCancelButton: { width: "100%", marginTop: 10, background: "none", border: "none", cursor: "pointer" }
};

export default Expense;